import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { PickTaskFilters } from './PickTaskFilterBar';
import { RepSort } from './PickTaskTable';
import {
  DataGridProperties,
  NewReportDataType,
  OverwriteReportDataType,
  SavedReportDataType,
  SavedReportsList,
} from '../../../@types/report.type';
import { updateReportByCode } from '../../../api/report';
import { SavedReport } from '../../../components/molecules/ReportTabs';
import { ColumnSelectionItem } from '../../../components/templates/ColumnSelection';
import { SavedReportTypeList } from '../../../configs/enums';
import {
  useAddNewReport,
  useGetDefaultReportByReportType,
  useGetReportByCode,
  useGetSavedReports,
  useUpdateBulkReports,
} from '../../../queries/report-query';
import { generateUniqueCode } from '../../../utils/common';
import { toast } from '../../../utils/toast';

const PAGE_NAME = 'PickTaskList';
const EMPTY: PickTaskFilters = { statuses: [], multiSearch: '', advancedFilters: [] };

// The pick-task list stores its own filter shape inside the shared
// dataGridProperties snapshot; extend the shared type locally rather than
// widening the cross-cutting DataGridProperties contract.
type PickTaskDataGridProperties = DataGridProperties & { pickTaskFilters?: PickTaskFilters };

interface UsePickTaskReportsOptions {
  currentFilters: PickTaskFilters;
  sortModel?: RepSort;
  pageSize: number;
  currentColumnSelection: ColumnSelectionItem[];
  onApply: (
    filters: PickTaskFilters,
    sortModel: RepSort | undefined,
    pageSize: number,
    columnSelection?: ColumnSelectionItem[]
  ) => void;
}

const toSnapshot = (
  filters: PickTaskFilters,
  sortModel: RepSort | undefined,
  pageSize: number
): PickTaskDataGridProperties => ({
  filterModel: { items: [] },
  pageSize,
  sortModel: sortModel ? [{ field: sortModel.field, sort: sortModel.sort }] : [],
  columnOrder: [],
  pickTaskFilters: filters,
});

const applySnapshot = (
  report: SavedReportDataType | undefined,
  onApply: UsePickTaskReportsOptions['onApply']
) => {
  const dgp = report?.dataGridProperties as PickTaskDataGridProperties | undefined;
  if (!dgp) return;
  const filters = dgp.pickTaskFilters ?? EMPTY;
  const sortItem = dgp.sortModel?.[0];
  const sortModel: RepSort | undefined = sortItem?.field
    ? { field: sortItem.field, sort: sortItem.sort === 'desc' ? 'desc' : 'asc' }
    : undefined;
  const columnSelection = report?.columnSelection?.length ? report.columnSelection : undefined;
  onApply(filters, sortModel, dgp.pageSize || 25, columnSelection);
};

export const usePickTaskReports = ({
  currentFilters,
  sortModel,
  pageSize,
  currentColumnSelection,
  onApply,
}: UsePickTaskReportsOptions) => {
  const [activeReport, setActiveReport] = useState<string | null>(null);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const defaultApplied = useRef(false);

  const addNewReport = useAddNewReport();
  const bulkUpdateReports = useUpdateBulkReports();
  const defaultReportQuery = useGetDefaultReportByReportType(SavedReportTypeList.PickTaskList);
  const reportsQuery = useGetSavedReports({
    filterModel: { items: [{ field: 'pageName', operator: 'equals', value: PAGE_NAME }] },
    page: 0,
    pageSize: 100,
  });
  const reportConfigQuery = useGetReportByCode(activeReport ?? '');

  useEffect(() => {
    reportsQuery.refetch();
    defaultReportQuery.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only initial fetch; the refetch fns are stable.
  }, []);

  useEffect(() => {
    if (defaultApplied.current || activeReport) return;
    const def = defaultReportQuery.data?.[0];
    if (def) {
      defaultApplied.current = true;
      applySnapshot(def, onApply);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- apply the default snapshot once, only when the default report data first arrives.
  }, [defaultReportQuery.data]);

  const savedReports: SavedReport[] = useMemo(() => {
    if (!reportsQuery.data?.data) return [];
    return reportsQuery.data.data.map((r: SavedReportsList) => ({
      id: r.id,
      code: r.code,
      name: r.name,
      type: (r.savedReportAccess?.length > 0 ? 'shared' : 'my') as 'my' | 'shared',
      isDefault: r.isDefault,
    }));
  }, [reportsQuery.data]);

  const activeReportName = useMemo(
    () => savedReports.find((r) => r.code === activeReport)?.name ?? null,
    [savedReports, activeReport]
  );

  const defaultTabLabel = useMemo(() => {
    const def = defaultReportQuery.data?.[0];
    return def?.name ?? 'All Pick Tasks';
  }, [defaultReportQuery.data]);

  useEffect(() => {
    if (activeReport) reportConfigQuery.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch when the active report changes; the refetch fn is stable.
  }, [activeReport]);

  useEffect(() => {
    if (reportConfigQuery.data && activeReport) applySnapshot(reportConfigQuery.data, onApply);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- apply the snapshot when the selected report's config loads; onApply is stable.
  }, [reportConfigQuery.data]);

  const handleReportChange = useCallback(
    (code: string | null) => {
      setActiveReport(code);
      if (code === null) {
        const def = defaultReportQuery.data?.[0];
        if (def) applySnapshot(def, onApply);
        else onApply(EMPTY, undefined, 25);
      }
    },
    [defaultReportQuery.data, onApply]
  );

  const refresh = useCallback(() => {
    reportsQuery.refetch();
    defaultReportQuery.refetch();
  }, [reportsQuery, defaultReportQuery]);

  const setReportAsDefault = useCallback(
    async (targetId: number) => {
      const currentDefault = savedReports.find((r) => r.isDefault);
      const updates: Array<{ id: number; isDefault: boolean }> = [
        { id: targetId, isDefault: true },
      ];
      if (currentDefault && currentDefault.id !== targetId) {
        updates.unshift({ id: currentDefault.id, isDefault: false });
      }
      await bulkUpdateReports.mutateAsync(updates);
      refresh();
    },
    [savedReports, bulkUpdateReports, refresh]
  );

  const handleSaveNewReport = useCallback(
    async (name: string, setAsDefault: boolean) => {
      try {
        const result = await addNewReport.mutateAsync({
          name,
          code: generateUniqueCode(),
          reportTypeName: SavedReportTypeList.PickTaskList,
          isDefault: setAsDefault,
          dataGridProperties: toSnapshot(currentFilters, sortModel, pageSize),
          columnSelection: currentColumnSelection,
        } as NewReportDataType);
        if (setAsDefault && result?.id) await setReportAsDefault(result.id);
        refresh();
        setSaveDialogOpen(false);
        toast.success('Report saved');
      } catch {
        toast.error('Failed to save report');
      }
    },
    [
      addNewReport,
      currentFilters,
      sortModel,
      pageSize,
      currentColumnSelection,
      setReportAsDefault,
      refresh,
    ]
  );

  const handleOverwriteReport = useCallback(
    async (setAsDefault: boolean) => {
      if (!activeReport) return;
      const target = savedReports.find((r) => r.code === activeReport);
      if (!target) {
        toast.error('Report not found');
        return;
      }
      try {
        await updateReportByCode(activeReport, {
          name: target.name,
          isDefault: target.isDefault,
          dataGridProperties: toSnapshot(currentFilters, sortModel, pageSize),
          columnSelection: currentColumnSelection,
        } as OverwriteReportDataType);
        if (setAsDefault) await setReportAsDefault(target.id);
        refresh();
        setSaveDialogOpen(false);
        toast.success('Report updated');
      } catch {
        toast.error('Failed to update report');
      }
    },
    [
      activeReport,
      savedReports,
      currentFilters,
      sortModel,
      pageSize,
      currentColumnSelection,
      setReportAsDefault,
      refresh,
    ]
  );

  const handleSetDefault = useCallback(
    (report: SavedReport) => setReportAsDefault(report.id),
    [setReportAsDefault]
  );

  return {
    savedReports,
    activeReport,
    activeReportName,
    defaultTabLabel,
    saveDialogOpen,
    setSaveDialogOpen,
    handleReportChange,
    handleSaveNewReport,
    handleOverwriteReport,
    handleSetDefault,
  };
};
